import { useState, useCallback, useEffect, useRef } from 'react';
import type { AppConfig, LightsConfig, BacklineConfig, StageDesignConfig, GeneralConfig, SpecsConfig, StageRack } from '../types';
import { createDefaultAppConfig, migrateAppConfig } from '../types';
import { useProfileContext } from './useProfileContext';
import { useRealtime } from './useRealtime';
import { apiGetConfig, apiSaveConfig, ApiError } from '../api';
import type { RackConflictWarning, ConfigSaveWarning } from '../api';
import { reportSaveError, reportSaveOk, isShowcaseMode, subscribeSaveErrors } from '../lib/saveStatus';
import { pushConfigNotice, CONFIG_CONFLICT_MESSAGE } from '../lib/configGuard';
import { pickTouchedUnits } from '../lib/configTouched';
import { useWriteGate } from '../lib/writeGate';

// Config transport modes (section x transport is the anti-loss merge unit).
const CONFIG_TRANSPORTS = ['van', 'plane', 'tourbus', 'train'];
// Reserved control keys the server may add to (or accept in) the config blob.
// They are NEVER part of the in-memory state.
const RESERVED_CONFIG_KEYS = ['__lastModified', '__touched', '__baseVersion', '__forceEmpty'];

/** The concurrency VERSION of the config we last loaded (`last_modified`). */
function extractConfigVersion(raw: unknown): number | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const v = (raw as Record<string, unknown>).__lastModified;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Strip the reserved control keys before the blob reaches the UI state (so an
 *  autosave never echoes them back into the stored config). */
function sanitizeConfigForState(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const clean: Record<string, unknown> = { ...(raw as Record<string, unknown>) };
  for (const k of RESERVED_CONFIG_KEYS) delete clean[k];
  return clean;
}

/** Map a section patch to the `__touched` entries it implies. A patch shaped
 *  `{ van: {...} }` marks `section.van`; any other shape marks the whole section. */
function touchedKeysForSection(section: string, patch: Record<string, unknown>): string[] {
  const keys = Object.keys(patch || {});
  const out: string[] = [];
  for (const k of keys) if (CONFIG_TRANSPORTS.includes(k)) out.push(`${section}.${k}`);
  return out.length > 0 ? out : [section];
}

export function useConfig() {
  const { activeProfileId } = useProfileContext();
  const { refreshToken } = useRealtime();
  // Capability-aware WRITE gate (lot A2-CAP, 2026-09-25). This hook is the ONLY
  // path to `PUT /api/bands/:id/rider/config` (the rider CONTENT) — gating the
  // write HERE covers every config page at once. A globally `read-only` account
  // keeps the write only through `content.rider` (conditional pre-guard mount in
  // server/index.js); everything else is refused there with 403. Same rule as the
  // per-page `disabled` freeze, never the blanket `canWrite` — that would freeze
  // the very crew member the capability exists for.
  const writeGate = useWriteGate();
  const canWriteContent = writeGate.canWriteCap('content.rider');
  const [config, setConfig] = useState<AppConfig>(createDefaultAppConfig);
  const [loaded, setLoaded] = useState(false);
  // Non-blocking rack-occupation warnings from the last config save (mode
  // AVERTIR). Empty when the last save had no conflicts. (chantier #2)
  const [rackWarnings, setRackWarnings] = useState<RackConflictWarning[]>([]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipSave = useRef(true);
  // Snapshot of the last state persisted (or fetched) from the server. The
  // autosave skips when nothing changed, which is what stops a real-time
  // refetch (band-updated) from writing the freshly-received state straight
  // back — the cross-client write-back loop.
  const lastSavedRef = useRef<string | null>(null);
  // Anti data-loss state (2026-10-07):
  //  - versionRef: the `rider_config.last_modified` we loaded; echoed on the PUT
  //    so the server can refuse a stale write (409).
  //  - touchedRef: sections the user REALLY edited this session (section or
  //    section.transport) so the server MERGES instead of full-replacing.
  //  - forceFullRef: a deliberate full reset — send no `__touched`.
  const versionRef = useRef<number | null>(null);
  const touchedRef = useRef<string[]>([]);
  const forceFullRef = useRef(false);
  // Stable handle to `reload` (defined further down) so the save effect's error
  // path can refetch the latest config without a TDZ / dependency cycle.
  const reloadRef = useRef<() => Promise<void>>(async () => {});
  const markTouched = useCallback((keys: string[]) => {
    if (!keys || keys.length === 0) return;
    const set = new Set(touchedRef.current);
    for (const k of keys) set.add(k);
    touchedRef.current = [...set];
  }, []);

  // --- Load config from server when profile changes ---
  useEffect(() => {
    if (!activeProfileId) {
      setConfig(createDefaultAppConfig());
      setLoaded(true);
      setRackWarnings([]);
      skipSave.current = true;
      return;
    }

    let cancelled = false;
    skipSave.current = true;

    (async () => {
      try {
        const serverConfig = await apiGetConfig(activeProfileId);
        if (!cancelled) {
          // Capture the concurrency version + drop the reserved control keys
          // BEFORE the blob reaches the UI (so autosave never echoes them).
          versionRef.current = extractConfigVersion(serverConfig);
          const cleanRaw = sanitizeConfigForState(serverConfig);
          let next: AppConfig;
          if (cleanRaw) {
            // Normalize any legacy/partial config (v1/v2/dual-preset/migrated)
            // into the full AppConfig shape so pages never read undefined.
            next = migrateAppConfig(cleanRaw);
          } else {
            next = createDefaultAppConfig();
          }
          touchedRef.current = [];
          forceFullRef.current = false;
          lastSavedRef.current = JSON.stringify(next);
          setConfig(next);
          setLoaded(true);
          skipSave.current = false;
        }
      } catch (err) {
        console.error('[useConfig] Failed to load config from API:', err);
        // SECURITY: on load failure, NEVER replace the config with defaults.
        // Doing so and re-enabling the debounced save would push those defaults
        // to the server and destroy the band's real rider_config.
        // Keep previous state and stay in skipSave mode. (Same guard as
        // useMusicians / useCrew.)
        if (!cancelled) {
          skipSave.current = true;
          reportSaveError('config', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeProfileId]);

  // --- Debounced save to server ---
  useEffect(() => {
    // Capability-aware gate (lot A2-CAP): a globally `read-only` account without
    // `content.rider` never fires the write — the server would answer 403
    // `read-only account` and the app would show a bogus « Not saved » banner.
    // Placed BEFORE the anti-wipe guard so the capability is re-read on every
    // change (a grant / revoke is honoured without a reload).
    if (!canWriteContent) { skipSave.current = true; return; }
    if (skipSave.current || !activeProfileId || !loaded) return;
    // Showcase (demo) account: NEVER autosave. The server refuses the write
    // (read-only guard), and attempting it is what produced the bogus
    // "⚠️ Not saved / API 401" banner right after the demo login. (2026-09-15)
    if (isShowcaseMode()) return;

    // Skip when nothing actually changed since the last persist/fetch — this
    // is the anti-loop guard for real-time refetches.
    if (JSON.stringify(config) === lastSavedRef.current) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(() => {
      // Snapshot the version + edits tracked so far. `forceFullRef` (a deliberate
      // reset) sends NO `__touched` => historical full replace.
      const touchedSnapshot = forceFullRef.current ? [] : [...touchedRef.current];
      // Ship ONLY the units this session edited (2026-10-15). The server keeps the
      // STORED value for every unit absent from `__touched` (non-destructive
      // merge), so sending the whole blob was pure weight — and it was the ONLY
      // cause of the on-screen « preserved » notices: an untouched unit that is
      // empty client-side arrived empty, i.e. it looked like a deliberate wipe.
      // An EMPTY touched list keeps the historical full payload (deliberate reset
      // via forceFullRef, or an older cached bundle) — never reduce that path.
      const payload = touchedSnapshot.length > 0 ? pickTouchedUnits(config, touchedSnapshot) : config;
      apiSaveConfig(activeProfileId, payload, {
        touched: touchedSnapshot,
        version: versionRef.current,
      })
        .then((res) => {
          lastSavedRef.current = JSON.stringify(config);
          if (typeof res.version === 'number') versionRef.current = res.version;
          // Forget ONLY the entries we just persisted; edits made while the
          // request was in flight stay tracked for the next save.
          if (touchedSnapshot.length > 0) {
            const sent = new Set(touchedSnapshot);
            touchedRef.current = touchedRef.current.filter((k) => !sent.has(k));
          }
          forceFullRef.current = false;
          const warns: ConfigSaveWarning[] = res.warnings || [];
          const rack = warns.filter((w): w is RackConflictWarning => (w as { type?: string }).type !== 'preserved');
          setRackWarnings(rack);
          // Surface (never silently) every value the server KEPT on our behalf.
          for (const w of warns) {
            const p = w as { type?: string; message?: string; section?: string };
            if (p.type === 'preserved' && typeof p.message === 'string' && p.message) {
              pushConfigNotice('preserved', p.message, typeof p.section === 'string' ? p.section : undefined);
            }
          }
          reportSaveOk('config');
        })
        .catch((err) => {
          // CONCURRENCY GUARD: another session wrote after we loaded. The server
          // wrote NOTHING. Warn visibly and reload the latest version.
          if (err instanceof ApiError && err.status === 409 && (err.body as { error?: string })?.error === 'stale') {
            pushConfigNotice('conflict', CONFIG_CONFLICT_MESSAGE);
            void reloadRef.current().catch(() => undefined);
            return;
          }
          console.error('[useConfig] Failed to save config to API:', err);
          reportSaveError('config', err);
        });
    }, 800); // 800ms debounce

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [config, activeProfileId, loaded, canWriteContent]);

  const updateSound = useCallback(
    (patch: Partial<AppConfig['sound']>) => {
      markTouched(touchedKeysForSection('sound', patch as Record<string, unknown>));
      setConfig((prev) => ({
        ...prev,
        sound: { ...prev.sound, ...patch },
      }));
    },
    [markTouched],
  );

  const updateIem = useCallback(
    (patch: Partial<AppConfig['iem']>) => {
      markTouched(touchedKeysForSection('iem', patch as Record<string, unknown>));
      setConfig((prev) => ({
        ...prev,
        iem: { ...prev.iem, ...patch },
      }));
    },
    [markTouched],
  );

  const updateStage = useCallback(
    (patch: Partial<AppConfig['stage']>) => {
      markTouched(touchedKeysForSection('stage', patch as Record<string, unknown>));
      setConfig((prev) => ({
        ...prev,
        stage: { ...prev.stage, ...patch },
      }));
    },
    [markTouched],
  );

  const updateHospitality = useCallback(
    (patch: Partial<AppConfig['hospitality']>) => {
      markTouched(touchedKeysForSection('hospitality', patch as Record<string, unknown>));
      setConfig((prev) => ({
        ...prev,
        hospitality: { ...prev.hospitality, ...patch },
      }));
    },
    [markTouched],
  );

  const updateLights = useCallback(
    (patch: Partial<LightsConfig>) => {
      markTouched(touchedKeysForSection('lights', patch as Record<string, unknown>));
      setConfig((prev) => ({
        ...prev,
        lights: { ...prev.lights, ...patch },
      }));
    },
    [markTouched],
  );

  const updateBackline = useCallback(
    (patch: Partial<BacklineConfig>) => {
      markTouched(touchedKeysForSection('backline', patch as Record<string, unknown>));
      setConfig((prev) => ({
        ...prev,
        backline: { ...prev.backline, ...patch },
      }));
    },
    [markTouched],
  );

  const updateStageDesign = useCallback(
    (patch: Partial<StageDesignConfig>) => {
      markTouched(touchedKeysForSection('stageDesign', patch as Record<string, unknown>));
      setConfig((prev) => ({
        ...prev,
        stageDesign: { ...prev.stageDesign, ...patch },
      }));
    },
    [markTouched],
  );

  const updateGeneral = useCallback(
    (patch: Partial<GeneralConfig>) => {
      markTouched(touchedKeysForSection('general', patch as Record<string, unknown>));
      setConfig((prev) => ({
        ...prev,
        general: { ...prev.general, ...patch },
      }));
    },
    [markTouched],
  );

  const updateSpecs = useCallback(
    (patch: Partial<SpecsConfig>) => {
      markTouched(touchedKeysForSection('specs', patch as Record<string, unknown>));
      setConfig((prev) => ({
        ...prev,
        specs: { ...prev.specs, ...patch },
      }));
    },
    [markTouched],
  );

  const updateGlobalRacks = useCallback(
    (racks: StageRack[]) => {
      markTouched(['globalRacks']);
      setConfig((prev) => ({ ...prev, globalRacks: racks }));
    },
    [markTouched],
  );

  const updateRider = useCallback(
    (patch: Partial<AppConfig['rider']>) => {
      markTouched(['rider']);
      setConfig((prev) => ({
        ...prev,
        rider: { ...prev.rider, ...patch },
      }));
    },
    [markTouched],
  );

  const updateSetlistPdf = useCallback(
    (patch: Partial<AppConfig['setlistPdf']>) => {
      markTouched(['setlistPdf']);
      setConfig((prev) => ({
        ...prev,
        // `columns`/`style` are required on the type but may be absent from a
        // saved config; seed empty defaults so the spread always satisfies it.
        setlistPdf: { columns: {}, style: {}, ...(prev.setlistPdf || {}), ...patch },
      }));
    },
    [markTouched],
  );

  const resetConfig = useCallback(() => {
    // A deliberate full reset: forget per-section tracking and mark the next
    // save as a historical full replace (no `__touched`).
    forceFullRef.current = true;
    touchedRef.current = [];
    setConfig(createDefaultAppConfig());
  }, []);

  // Force a fresh fetch of the persisted config. Used after an external write
  // (e.g. the AI "Apply to config" flow persists via /api/ai/apply, which does
  // not flow through this hook's autosave) so the local state reflects the
  // merged result immediately. Reuses the SAME guard as the load effect: on
  // failure it keeps previous state, stays in skipSave mode and reports via the
  // save-error bus — it never replaces server data with defaults.
  const reload = useCallback(async () => {
    if (!activeProfileId) return;
    try {
      const serverConfig = await apiGetConfig(activeProfileId);
      versionRef.current = extractConfigVersion(serverConfig);
      const cleanRaw = sanitizeConfigForState(serverConfig);
      let next: AppConfig;
      if (cleanRaw) {
        next = migrateAppConfig(cleanRaw);
      } else {
        next = createDefaultAppConfig();
      }
      touchedRef.current = [];
      forceFullRef.current = false;
      lastSavedRef.current = JSON.stringify(next);
      setConfig(next);
      setLoaded(true);
      skipSave.current = false;
      reportSaveOk('config');
    } catch (err) {
      console.error('[useConfig] Failed to reload config from API:', err);
      skipSave.current = true;
      reportSaveError('config', err);
      throw err;
    }
  }, [activeProfileId]);

  // Keep the stable handle in sync (used by the save effect's 409 path).
  reloadRef.current = reload;

  // Real-time: when the active band is updated by another client, refetch the
  // config so this page stays in sync without a manual reload. The autosave
  // anti-loop guard (lastSavedRef) stops the refetched state being written
  // straight back, and self-broadcasts are suppressed server-side.
  useEffect(() => {
    if (refreshToken > 0 && activeProfileId) {
      void reload().catch(() => undefined);
    }
  }, [refreshToken, activeProfileId, reload]);

  return {
    config,
    rackWarnings,
    updateSound,
    updateIem,
    updateStage,
    updateHospitality,
    updateLights,
    updateBackline,
    updateStageDesign,
    updateGeneral,
    updateSpecs,
    updateGlobalRacks,
    updateRider,
    updateSetlistPdf,
    resetConfig,
    reload,
  };
}

/**
 * Lot 3a (2026-10-14) — did the last CONFIG read fail?
 *
 * The config pages read through `useConfig`, which deliberately never replaces
 * the state with defaults on a failed load (that would let the debounced
 * autosave push those defaults over the band's real rider_config — a real
 * data-loss bug). The flip side: the page renders EMPTY fields and the reader
 * cannot tell "the band typed nothing" from "the request failed".
 *
 * This hook exposes that failure as a readable flag, without touching the hook's
 * load/save logic at all: it subscribes to the SAME save-error bus the failure
 * already reports through, and clears when the config reports success. A page
 * that pairs it with `<PageLoadError onRetry={() => reload()}>` therefore shows
 * a real state instead of a void. The message carried on the bus is surfaced as
 * the small context line — never an invented cause.
 */
export function useConfigLoadError(): { failed: boolean; message: string | null } {
  const [state, setState] = useState<{ failed: boolean; message: string | null }>({
    failed: false,
    message: null,
  });

  useEffect(() => {
    return subscribeSaveErrors((entries) => {
      const configEntry = entries.find((e) => e.scope === 'config');
      setState(
        configEntry
          ? { failed: true, message: configEntry.message || null }
          : { failed: false, message: null },
      );
    });
  }, []);

  return state;
}
